/**
 * "Use a template", "Save as template" and "Manage rubric templates" beside the expert's rubric
 * editor. Templates are the expert's own (see lib/rubricTemplates). The management page has no
 * menu entry: this link, below a rubric, is the way in. Science experts, who don't write rubrics
 * in the task editor, get none of this.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { useProfile } from '@/auth/ProfileContext'
import { isScienceExpert } from '@/lib/programs'
import { Dialog } from './Dialog'
import {
  countLabel, getTemplate, listTemplates, MAX_TEMPLATE_NAME, nameProblem, sameName, saveTemplate, templateCriteria, templateProblem,
  TEMPLATES_ROUTE,
} from '@/lib/rubricTemplates'
import type { RubricCriterion, RubricTemplate, RubricTemplateSummary, TemplateCriterion } from '@/lib/types'

/** `room`: how many criteria the rubric can still take. */
export function RubricTemplateMenu({ rubric, room, onAdd }: {
  rubric: RubricCriterion[]
  room: number
  onAdd: (criteria: TemplateCriterion[], name: string) => void
}) {
  const { profile } = useProfile()
  const [open, setOpen] = useState<'use' | 'save' | null>(null)
  if (isScienceExpert(profile)) return null
  return <>
    <button type="button" onClick={() => setOpen('use')}>Use a template</button>
    <button type="button" onClick={() => setOpen('save')}>Save as template</button>
    <ManageLink className="rubric-manage-templates" />
    {open === 'use' && <UseTemplate room={room} onClose={() => setOpen(null)} onAdd={(criteria, name) => { setOpen(null); onAdd(criteria, name) }} />}
    {open === 'save' && <SaveTemplate rubric={rubric} onClose={() => setOpen(null)} />}
  </>
}

/**
 * The expert's template list while a dialog is open; reloaded when the window regains focus, e.g.
 * after editing templates in the tab "Manage rubric templates" opened. Only the latest load counts.
 */
function useTemplates() {
  const [list, setList] = useState<{ templates: RubricTemplateSummary[]; limit: number } | null>(null)
  const [error, setError] = useState('')
  const generation = useRef(0)
  const load = useCallback(() => {
    const current = ++generation.current
    setError('')
    listTemplates().then(
      (result) => { if (current === generation.current) setList(result) },
      (e: Error) => { if (current === generation.current) setError(e.message) },
    )
  }, [])
  useEffect(() => {
    load()
    window.addEventListener('focus', load)
    return () => { generation.current++; window.removeEventListener('focus', load) }
  }, [load])
  return { templates: list?.templates ?? null, limit: list?.limit ?? Infinity, error, load }
}

function ManageLink({ className = 'small' }: { className?: string }) {
  // A new tab, so an unsaved rubric stays as it is.
  return <Link to={TEMPLATES_ROUTE} target="_blank" rel="noreferrer" className={className}>Manage rubric templates</Link>
}

function ListError({ error, retry }: { error: string; retry: () => void }) {
  return <p className="note err" role="alert">{error} <button type="button" className="link small" onClick={retry}>Try again</button></p>
}

function UseTemplate({ room, onClose, onAdd }: { room: number; onClose: () => void; onAdd: (criteria: TemplateCriterion[], name: string) => void }) {
  const { templates, error: listError, load } = useTemplates()
  const [chosen, setChosen] = useState<RubricTemplate | null>(null)
  const [opening, setOpening] = useState('')
  const [error, setError] = useState('')
  const live = useRef(true)
  useEffect(() => { live.current = true; return () => { live.current = false } }, [])

  async function open(id: string) {
    setOpening(id); setError('')
    try {
      const template = await getTemplate(id)
      if (live.current) setChosen(template)
    } catch (e) {
      if (!live.current) return
      setError((e as Error).message)
      if (e instanceof ApiError && e.status === 404) load()  // deleted in another tab
    } finally {
      if (live.current) setOpening('')
    }
  }

  const fits = !chosen || chosen.criteria.length <= room
  return <Dialog title={chosen ? chosen.name : 'Use a rubric template'} view={chosen ? `preview:${chosen.template_id}` : templates ? 'list' : 'loading'} onClose={onClose}>
    {chosen ? <>
      <p className="muted small">These criteria are added to your rubric. You can edit them there; the template stays as it is.</p>
      <TemplatePreview criteria={chosen.criteria} />
      {!fits && <p className="note warn">Your rubric has room for {countLabel(room)}; this template has {chosen.criteria.length}. Remove some criteria first.</p>}
      <div className="row">
        <button type="button" onClick={() => setChosen(null)}>Back</button>
        <button type="button" className="primary" data-autofocus disabled={!fits} onClick={() => onAdd(chosen.criteria, chosen.name)}>Add {countLabel(chosen.criteria.length)}</button>
      </div>
    </> : <>
      {listError && <ListError error={listError} retry={load} />}
      {error && <p className="note err" role="alert">{error}</p>}
      {templates === null ? !listError && <p className="muted" role="status">Loading your templates…</p>
        : templates.length === 0 ? <p className="muted">You have no rubric templates yet. Save a rubric as a template, or create one with “Manage rubric templates”.</p>
        : <ul className="template-pick">
          {templates.map((t) => <li key={t.template_id}>
            <button type="button" disabled={!!opening} onClick={() => void open(t.template_id)}>
              <strong>{t.name}</strong>
              <span className="muted small">{opening === t.template_id ? 'Opening…' : countLabel(t.criteria_count)}</span>
            </button>
          </li>)}
        </ul>}
      <div className="row spread">
        <ManageLink />
        <button type="button" onClick={onClose}>Cancel</button>
      </div>
    </>}
  </Dialog>
}

function SaveTemplate({ rubric, onClose }: { rubric: RubricCriterion[]; onClose: () => void }) {
  const { templates, limit, error: listError, load } = useTemplates()
  const [name, setName] = useState('')
  // The template being replaced, as it was when the expert chose it: a save made elsewhere since is a 412.
  const [base, setBase] = useState<RubricTemplateSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [changed, setChanged] = useState(false)
  const [saved, setSaved] = useState('')
  const problem = templateProblem(rubric)
  const badName = name.trim() ? nameProblem(name) : ''
  const criteria = templateCriteria(rubric)
  // Names are unique per expert, ignoring case: no other template may have this one.
  const clash = name.trim() ? templates?.find((t) => t.template_id !== base?.template_id && sameName(t.name, name)) : undefined
  const full = !base && !!templates && templates.length >= limit
  // The template being replaced was deleted elsewhere (the list reloads on focus).
  const gone = !!base && !!templates && !templates.some((t) => t.template_id === base.template_id)

  function choose(id: string) {
    const next = templates?.find((t) => t.template_id === id) ?? null
    // Prefill with the replaced template's name, without overwriting a name the expert typed.
    if (!name.trim() || name === base?.name) setName(next?.name ?? '')
    setBase(next); setChanged(false); setError('')
  }
  async function save(target = base) {
    setBusy(true); setError(''); setChanged(false)
    try {
      const template = await saveTemplate(name, criteria, target ?? undefined)
      setSaved(`Saved “${template.name}” with ${countLabel(template.criteria_count)}.`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 412) setChanged(true)
      else setError((e as Error).message)
      if (e instanceof ApiError && [404, 409, 412].includes(e.status)) load()
    } finally {
      setBusy(false)
    }
  }
  /** After a 412: replace the template as it is now, read afresh rather than from the reloading list. */
  async function replaceAnyway() {
    if (!base) return
    setBusy(true); setError('')
    try {
      const current = await getTemplate(base.template_id)
      setBase({ ...base, updated_at: current.updated_at, criteria_count: current.criteria_count })
      await save({ ...base, updated_at: current.updated_at })
    } catch (e) {
      setError((e as Error).message)
      if (e instanceof ApiError && e.status === 404) load()
      setBusy(false)
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault()
    if (!busy && !problem && name.trim() && !badName && !clash && !full && !gone && !changed && templates) void save()
  }

  return <Dialog title={saved ? 'Template saved' : 'Save as rubric template'} busy={busy} onClose={onClose}>
    {saved ? <>
      <p role="status">{saved}</p>
      <div className="row spread"><ManageLink /><button type="button" className="primary" data-autofocus onClick={onClose}>Done</button></div>
    </> : <form onSubmit={submit}>
      {problem ? <p className="note warn">{problem}</p> : <>
        <p className="muted small">Saves the {countLabel(criteria.length)} written in this rubric, with their roles and how you would check them. Only you can see your templates.</p>
        {listError && <ListError error={listError} retry={load} />}
        {(!!templates?.length || gone) && <label className="field">
          <span>Save to</span>
          <select value={base?.template_id ?? ''} disabled={busy} onChange={(e) => choose(e.target.value)}>
            <option value="">A new template</option>
            {templates?.map((t) => <option key={t.template_id} value={t.template_id}>Replace “{t.name}”</option>)}
            {gone && <option value={base.template_id} disabled>“{base.name}” (deleted)</option>}
          </select>
        </label>}
        <label className="field">
          <span>Template name</span>
          <input type="text" value={name} maxLength={MAX_TEMPLATE_NAME} disabled={busy} data-autofocus onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Standard drawing checks" />
        </label>
        {badName && <p className="note warn" role="status">{badName}</p>}
        {clash && <p className="note warn" role="status">You already have a template called “{clash.name}”. Choose another name, or pick it under “Save to” to replace it.</p>}
        {full && <p className="note warn" role="status">You have {templates?.length} templates, the most you can keep. Replace one, or delete one with “Manage rubric templates”.</p>}
        {gone && <p className="note warn">“{base.name}” was deleted elsewhere. Choose “A new template” to save these criteria.</p>}
        {base && !gone && !changed && <p className="note warn">This replaces the {countLabel(base.criteria_count)} in “{base.name}” with the ones from this rubric.</p>}
        {changed && base && !gone && <p className="note warn" role="alert">
          “{base.name}” was changed elsewhere since you chose it.{' '}
          <button type="button" className="link small" disabled={busy} onClick={() => void replaceAnyway()}>Replace it anyway</button>
        </p>}
      </>}
      {error && <p className="note err" role="alert">{error}</p>}
      <div className="row">
        <button type="button" disabled={busy} onClick={onClose}>Cancel</button>
        {!problem && <button type="submit" className="primary" disabled={busy || !name.trim() || !!badName || !!clash || full || gone || changed || templates === null}>
          {busy ? 'Saving…' : base ? 'Replace template' : 'Save template'}
        </button>}
      </div>
    </form>}
  </Dialog>
}

function TemplatePreview({ criteria }: { criteria: TemplateCriterion[] }) {
  return <ol className="template-preview">
    {criteria.map((c, i) => <li key={i}>
      <span className="chip">{c.role}</span> {c.criterion}
      {c.evidence && <div className="muted small">Check: {c.evidence}</div>}
    </li>)}
  </ol>
}
